const { legalPageFromPath } = require('../utils/legal');
const { isReportTermsPath, reportShareFromPath } = require('./homeHistoryReport');
const { claimFromPath } = require('./homeTransfer');

const HOMEOWNER_PATHS = new Set([
  '/property',
  '/property-details',
  '/contacts',
  '/property-contacts',
]);

const AUTH_PATHS = new Set([
  '/login',
  '/signup',
  '/homeowner/signup',
  '/homeowner/plan',
]);

function normalizeAppPath(pathname) {
  const path = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
  return path || '/';
}

function taskActionFromLocation(pathname, search) {
  const path = normalizeAppPath(pathname);
  const match = /^\/tasks\/([^/]+)$/.exec(path);
  if (!match) return null;
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  return {
    taskId: decodeURIComponent(match[1]),
    action: params.get('action') || '',
    token: params.get('token') || '',
    scope: params.get('scope') || '',
  };
}

function isKnownAppPath(pathname) {
  const path = normalizeAppPath(pathname);
  if (path === '/' || path === '/auth/confirm') return true;
  if (taskActionFromLocation(path, '')) return true;
  if (AUTH_PATHS.has(path) || HOMEOWNER_PATHS.has(path)) return true;
  if (path === '/join' || path === '/support') return true;
  if (claimFromPath(path) || reportShareFromPath(path) || isReportTermsPath(path)) return true;
  return Boolean(legalPageFromPath(path));
}

/**
 * /login and /signup are direct addresses. /signup?role=agent opens agent
 * signup. /login?role=homeowner opens the homeowner invite form.
 */
function authScreenFromLocation(pathname, search) {
  const path = normalizeAppPath(pathname);
  if (!AUTH_PATHS.has(path)) return null;
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const role = String(params.get('role') || '').trim().toLowerCase();
  if (path === '/signup') {
    return { path, mode: 'signup', userType: 'agent', clientLoginMode: 'invite' };
  }
  if (role === 'homeowner' || role === 'client') {
    return { path, mode: 'signin', userType: 'client', clientLoginMode: 'invite' };
  }
  return { path, mode: 'signin', userType: 'agent', clientLoginMode: 'invite' };
}

/**
 * Signed-out visitors used to see the in-app marketing views at /.
 * Those views (role chooser, homeowner Get Started, agent Learn More)
 * are gone, so a bare / opens the login screen.
 * A query string stays put so invite, reset, checkout, and confirm
 * links are not rewritten before the screen that reads them.
 */
function loginRoleFromSearch(search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const login = String(params.get('login') || '').trim().toLowerCase();
  if (login === 'homeowner') {
    return { userType: 'client', clientLoginMode: 'password', loginMode: 'signin' };
  }
  if (login === 'agent') {
    return { userType: 'agent', clientLoginMode: 'invite', loginMode: 'signin' };
  }
  return null;
}

function isHomeownerSignupPath(pathname) {
  return normalizeAppPath(pathname) === '/homeowner/signup';
}

function isHomeownerPlanPath(pathname) {
  return normalizeAppPath(pathname) === '/homeowner/plan';
}

/** After a successful sign-in, /login and /signup become the home screen. */
function pathAfterSignIn(pathname) {
  const path = normalizeAppPath(pathname);
  if (path === '/login' || path === '/signup') return '/';
  return null;
}

function loginRedirectFromLocation(pathname, search) {
  const path = normalizeAppPath(pathname);
  if (path !== '/') return null;
  const query = String(search || '').replace(/^\?/, '');
  if (query) return null;
  return '/login';
}

export {
  isKnownAppPath,
  normalizeAppPath,
  authScreenFromLocation,
  pathAfterSignIn,
  loginRedirectFromLocation,
  loginRoleFromSearch,
  taskActionFromLocation,
  isHomeownerSignupPath,
  isHomeownerPlanPath,
};
